import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMessages } from 'contexts/MessagesContext';
import { useLeague } from 'contexts/LeagueContext';
import { useAuth } from 'contexts/AuthContext';
import NavigationBar from 'components/ui/NavigationBar';
import Icon from 'components/AppIcon';

const MessagesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { leagueMembers } = useLeague();
  const { conversations, loading } = useMessages();

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-heading font-bold text-xl" style={{ color: 'var(--color-foreground)' }}>
              Messages
            </h1>
            <button
              onClick={() => navigate('/members')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-caption text-xs font-medium transition-all"
              style={{ backgroundColor: 'rgba(232,93,4,0.12)', color: 'var(--color-primary)' }}
            >
              <Icon name="UserPlus" size={14} color="currentColor" />
              New Message
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12 rounded-xl border" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
              <Icon name="MessageSquare" size={40} color="var(--color-muted-foreground)" />
              <p className="font-caption text-sm mt-3 mb-1" style={{ color: 'var(--color-muted-foreground)' }}>
                No messages yet
              </p>
              <button
                onClick={() => navigate('/members')}
                className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 rounded-lg font-caption text-sm font-medium"
                style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
              >
                <Icon name="Users" size={16} color="#fff" />
                Message a Member
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => {
                const member = leagueMembers.find((m) => m.userId === conv.partnerId);
                const isUnread = conv.unreadCount > 0;
                const lastMsg = conv.lastMessage;
                const isSentByMe = lastMsg?.sender_id === user?.id;

                return (
                  <button
                    key={conv.partnerId}
                    onClick={() => navigate(`/messages/${conv.partnerId}`)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border transition-all hover:border-[var(--color-primary)] text-left"
                    style={{
                      backgroundColor: isUnread ? 'rgba(232,93,4,0.04)' : 'var(--color-card)',
                      borderColor: isUnread ? 'rgba(232,93,4,0.2)' : 'var(--color-border)',
                    }}
                  >
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-muted)' }}>
                      <Icon name="User" size={18} color="var(--color-muted-foreground)" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span
                          className={`font-heading text-sm truncate ${isUnread ? 'font-bold' : 'font-semibold'}`}
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {conv.partnerName}
                        </span>
                        <span className="font-caption text-xs flex-shrink-0 ml-2" style={{ color: 'var(--color-muted-foreground)' }}>
                          {formatTime(lastMsg?.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {isSentByMe && (
                          <span className="font-caption text-xs flex-shrink-0" style={{ color: 'var(--color-muted-foreground)' }}>You: </span>
                        )}
                        <p
                          className={`font-caption text-xs truncate ${isUnread ? 'font-medium' : ''}`}
                          style={{ color: isUnread ? 'var(--color-foreground)' : 'var(--color-muted-foreground)' }}
                        >
                          {lastMsg?.content}
                        </p>
                      </div>
                    </div>

                    {/* Unread badge */}
                    {isUnread && (
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                      >
                        <span className="font-caption text-xs font-bold text-white">{conv.unreadCount}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
